/**
 * Compares the current automation form value to the last server snapshot for dirty detection.
 *
 * Form edits can leave values that are semantically equal to the server payload but fail
 * `isEqual` (e.g. `dayOfDelay` cleared to '' in TimingSection vs API `null`, media order
 * after checkbox toggles, select fields storing numeric strings).
 *
 * Normalization also strips Formik-only `_…` shadow fields, coerces `delayInterval` /
 * `hour` / `dayOfDelay` / `afterTrial` / `everyPayment` shapes, `reminderTypeId`
 * (scalar vs array / order, empty `[]` vs `null`), `stripePlan` (empty object vs null), and empty
 * `reportFilters` (`[]` vs `{}`), purchase `reminderType` (`some`/`any` vs each other vs
 * stored sellable type), purchase `delayInterval` `0`/`1` for `hour`/`minute`/`day`, and
 * `minute`/`hour`/`day` with interval 0 unified for “immediate”, and drops explicit `undefined` (API snapshots omit keys).
 */

import { cloneDeep, isEqual } from 'lodash';

import { AutomationItem } from './types';

/** Same ordering as useAutomationMedia / v2 — used only for stable dirty comparison */
const MEDIA_CANONICAL_ORDER = ['message', 'email', 'in-app', 'text'] as const;

function sortMediaForCompare(media: string[] | undefined): string[] | undefined {
  if (!media?.length) {
    return media;
  }
  return [...media].sort((a, b) => {
    const ia = (MEDIA_CANONICAL_ORDER as readonly string[]).indexOf(a);
    const ib = (MEDIA_CANONICAL_ORDER as readonly string[]).indexOf(b);
    const ra = ia === -1 ? 999 : ia;
    const rb = ib === -1 ? 999 : ib;
    if (ra !== rb) {
      return ra - rb;
    }
    return a.localeCompare(b);
  });
}

/** Form fields may use '' where API uses null — types omit string for some numeric fields */
function normalizeDayOfDelay(v: unknown): number | null {
  if (v === '' || v == null) {
    return null;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return v as number;
}

function normalizeHour(v: unknown): number | null {
  if (v === '' || v == null) {
    return null;
  }
  if (typeof v === 'string') {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? null : n;
  }
  return v as number;
}

/** Formik-only shadow fields (e.g. TriggerSelect `_triggerSelectValue`) — not on API snapshots */
function stripFormOnlyShadowKeys(automation: AutomationItem): void {
  const o = automation as Record<string, unknown>;
  for (const key of Object.keys(o)) {
    if (key.startsWith('_')) {
      delete o[key];
    }
  }
}

function normalizeDelayInterval(v: unknown): number {
  if (v === '' || v == null) {
    return 0;
  }
  if (typeof v === 'string') {
    const parsed = parseInt(v, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return v as number;
}

/**
 * StripePlanFields uses 0/1 for checkbox fields; the API often uses `null` when off.
 * Form controls may coerce checked state to string `"1"` in edge cases.
 */
function normalizeCheckbox01(v: unknown): number | null {
  if (v === 1 || v === true || v === '1') {
    return 1;
  }
  return null;
}

/** API may return a single id; multi-select stores an array — compare as sorted id sets */
function normalizeReminderTypeIdForDirty(
  v: AutomationItem['reminderTypeId']
): (number | string)[] | null {
  if (v == null || v === '') {
    return null;
  }
  const raw = Array.isArray(v) ? v : [v];
  const nums = raw.map((x) => Number(x));
  if (raw.every((_, i) => !Number.isNaN(nums[i]))) {
    const sorted = [...new Set(nums)].sort((a, b) => a - b);
    return sorted.length ? sorted : null;
  }
  const strSorted = [...new Set(raw.map(String))].sort();
  return strSorted.length ? strSorted : null;
}

/** Empty `{}` or missing id should match `null` for “no plan” */
function normalizeStripePlanForDirty(
  p: AutomationItem['stripePlan']
): AutomationItem['stripePlan'] {
  if (p == null || typeof p !== 'object' || Array.isArray(p)) {
    return null;
  }
  const id = (p as { id?: string }).id;
  if (id == null || id === '') {
    return null;
  }
  return {
    id: String(id),
    nickname: String((p as { nickname?: string }).nickname ?? ''),
  };
}

/** Custom automations use `reportFilters` as object; some payloads use `[]` for empty */
function normalizeReportFiltersForDirty(
  v: AutomationItem['reportFilters']
): AutomationItem['reportFilters'] {
  if (v == null) {
    return {};
  }
  if (Array.isArray(v)) {
    return v.length === 0
      ? {}
      : (v as unknown as AutomationItem['reportFilters']);
  }
  if (typeof v === 'object') {
    return omitUndefinedDeep(v as Record<string, unknown>) as AutomationItem['reportFilters'];
  }
  return {};
}

/**
 * Formik / cloneDeep can leave explicit `undefined` where API snapshots omit keys.
 * lodash isEqual treats `{ x: undefined }` and `{}` as unequal.
 */
function omitUndefinedDeep<T>(value: T): T {
  if (value === undefined) {
    return value;
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => omitUndefinedDeep(item)) as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (val !== undefined) {
      out[key] = omitUndefinedDeep(val);
    }
  }
  return out as T;
}

/**
 * Aligns known form/API mismatches so reverting edits compares equal to the server snapshot.
 */
function normalizeAutomationForDirtyCompare(
  automation: AutomationItem
): AutomationItem {
  const n = cloneDeep(automation);
  stripFormOnlyShadowKeys(n);
  n.dayOfDelay = normalizeDayOfDelay(n.dayOfDelay) as AutomationItem['dayOfDelay'];
  if (n.media?.length) {
    n.media = sortMediaForCompare(n.media) as string[];
  }
  n.hour = normalizeHour(n.hour);
  n.delayInterval = normalizeDelayInterval(n.delayInterval);
  n.afterTrial = normalizeCheckbox01(n.afterTrial) as AutomationItem['afterTrial'];
  n.everyPayment = normalizeCheckbox01(
    n.everyPayment
  ) as AutomationItem['everyPayment'];
  n.reminderTypeId = normalizeReminderTypeIdForDirty(
    n.reminderTypeId
  ) as AutomationItem['reminderTypeId'];
  n.stripePlan = normalizeStripePlanForDirty(n.stripePlan);
  n.reportFilters = normalizeReportFiltersForDirty(n.reportFilters);
  return omitUndefinedDeep(n) as AutomationItem;
}

/** Stored API rows may use `some`/`any` while the UI persists the sellable subtype (e.g. `workout_plan`) */
const SELLABLE_REMINDER_TYPES = new Set([
  'workout_plan',
  'group',
  'product',
  'package',
  'client',
]);

const PURCHASE_TRIGGER_PLACEHOLDER = new Set(['some', 'any']);

function alignPurchaseReminderTypesForDirty(a: string, b: string): [string, string] {
  const aa = a ?? '';
  const bb = b ?? '';

  if (aa === '' && (bb === 'some' || bb === 'any' || SELLABLE_REMINDER_TYPES.has(bb))) {
    return [bb, bb];
  }
  if (bb === '' && (aa === 'some' || aa === 'any' || SELLABLE_REMINDER_TYPES.has(aa))) {
    return [aa, aa];
  }

  // `any` vs `some` are both purchase trigger placeholders in the UI
  if (
    PURCHASE_TRIGGER_PLACEHOLDER.has(aa) &&
    PURCHASE_TRIGGER_PLACEHOLDER.has(bb) &&
    aa !== bb
  ) {
    return ['some', 'some'];
  }

  if (PURCHASE_TRIGGER_PLACEHOLDER.has(aa) && SELLABLE_REMINDER_TYPES.has(bb)) {
    return [bb, bb];
  }
  if (PURCHASE_TRIGGER_PLACEHOLDER.has(bb) && SELLABLE_REMINDER_TYPES.has(aa)) {
    return [aa, aa];
  }

  return [aa, bb];
}

/** Purchase / sellable triggers only */
function isPurchaseFlowReminderType(rt: string): boolean {
  if (!rt) {
    return false;
  }
  if (rt === 'all') {
    return true;
  }
  if (PURCHASE_TRIGGER_PLACEHOLDER.has(rt)) {
    return true;
  }
  return SELLABLE_REMINDER_TYPES.has(rt);
}

/**
 * UI allows 0 = “immediately”; some payloads use 1 as the minimum step.
 * Pairs (0,1) / (1,0) with the same delayType are aligned to 0.
 */
function alignDelayIntervalForDirtyPair(a: AutomationItem, b: AutomationItem): void {
  const da = a.delayType || 'day';
  const db = b.delayType || 'day';
  if (da !== db) {
    return;
  }
  if (!['hour', 'minute', 'day'].includes(da)) {
    return;
  }
  if (
    !isPurchaseFlowReminderType(String(a.reminderType ?? '')) ||
    !isPurchaseFlowReminderType(String(b.reminderType ?? ''))
  ) {
    return;
  }
  const ia = normalizeDelayInterval(a.delayInterval);
  const ib = normalizeDelayInterval(b.delayInterval);
  if ((ia === 0 && ib === 1) || (ia === 1 && ib === 0)) {
    a.delayInterval = 0;
    b.delayInterval = 0;
  }
}

/** When delayInterval is 0, these units are equivalent for “immediate” timing */
const IMMEDIATE_DELAY_TYPES = new Set(['minute', 'hour', 'day']);

function alignImmediateDelayTypeForPurchase(a: AutomationItem, b: AutomationItem): void {
  if (
    !isPurchaseFlowReminderType(String(a.reminderType ?? '')) ||
    !isPurchaseFlowReminderType(String(b.reminderType ?? ''))
  ) {
    return;
  }
  const ia = normalizeDelayInterval(a.delayInterval);
  const ib = normalizeDelayInterval(b.delayInterval);
  if (ia !== 0 || ib !== 0) {
    return;
  }
  const da = a.delayType || 'day';
  const db = b.delayType || 'day';
  if (
    !IMMEDIATE_DELAY_TYPES.has(da) ||
    !IMMEDIATE_DELAY_TYPES.has(db) ||
    da === db
  ) {
    return;
  }
  a.delayType = 'hour';
  b.delayType = 'hour';
}

/**
 * Compares the form automation to a single server snapshot row (same `id`).
 * Prefer this when the server row is already resolved (e.g. `Map.get`) to avoid
 * repeated linear scans over the full list for each card.
 */
export function isAutomationDirtyAgainstServerRow(
  automation: AutomationItem,
  server: AutomationItem | undefined
): boolean {
  if (automation.id == null) {
    return true;
  }
  if (!server) {
    return true;
  }
  const normAuto = normalizeAutomationForDirtyCompare(automation);
  const normServer = normalizeAutomationForDirtyCompare(server);
  const [rta, rts] = alignPurchaseReminderTypesForDirty(
    String(normAuto.reminderType ?? ''),
    String(normServer.reminderType ?? '')
  );
  normAuto.reminderType = rta as AutomationItem['reminderType'];
  normServer.reminderType = rts as AutomationItem['reminderType'];
  alignDelayIntervalForDirtyPair(normAuto, normServer);
  alignImmediateDelayTypeForPurchase(normAuto, normServer);
  return !isEqual(normAuto, normServer);
}

export function isAutomationDirty(
  automation: AutomationItem,
  serverAutomations: AutomationItem[]
): boolean {
  if (automation.id != null) {
    const server = serverAutomations.find((a) => a.id === automation.id);
    return isAutomationDirtyAgainstServerRow(automation, server);
  }
  return true;
}
